/**
 * 1.7.1 — Ready means ready. The pieces the list, the card and the two sheets share: what a volunteer is missing in
 * Elijah's words (never a bare status), the Services card (the six services as the office ticks them), and the
 * "n more signed up … but not Ready" line under a request's or an outing's count, each name opening that volunteer.
 */
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { SERVICE_KEYS, missingCounts, type ServiceKey } from '@shared/volunteers';
import type { AlmostView, OutingPickerView, VolunteerRowView } from '@shared/volunteers-api';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';
import { vid } from './api';

/** "Missing: background check — cannot be scheduled for Companion & Wellness", one line per thing. */
export function MissingLines({ v }: { v: Pick<VolunteerRowView, 'status' | 'missing' | 'recheck' | 'services'> & Pick<Partial<VolunteerRowView>, 'addedServices'> }) {
  const { t } = useTranslation();
  if (v.status !== 'signedUp') return null;
  // 1.8.3: a service the office added is a service ticked
  const lines = [...(v.services.length || v.addedServices?.length ? [] : (['service'] as const)), ...v.missing.filter((m) => m === 'waiver' || m === 'training' || m === 'background'), ...(v.recheck ? (['recheck'] as const) : [])];
  if (!lines.length) return null;
  return (
    <ul className="missing-lines" data-testid="vol-missing">
      {lines.map((m) => (
        <li key={m}>{t(`vol171.missing.${m}`)}</li>
      ))}
    </ul>
  );
}

/** The six services as ticks, a Save, and (on the server) an audit line per change. */
export function ServicesCard({ services, disabled, onSave }: { services: ServiceKey[]; disabled: boolean; onSave: (next: ServiceKey[]) => void }) {
  const { t } = useTranslation();
  const uid = useId();
  const [picked, setPicked] = useState<ServiceKey[]>(services);
  const key = services.join(',');
  useEffect(() => setPicked(services), [key]); // eslint-disable-line react-hooks/exhaustive-deps -- the saved ticks, by value
  const changed = SERVICE_KEYS.some((s) => picked.includes(s) !== services.includes(s));
  return (
    <section aria-labelledby={`${uid}-h`} data-testid="services-card">
      <h3 className="eyebrow" id={`${uid}-h`}>
        {t('vol171.services.title')}
      </h3>
      <ul className="checklist" aria-labelledby={`${uid}-h`}>
        {SERVICE_KEYS.map((s) => (
          <li key={s}>
            <input
              id={`${uid}-${s}`}
              type="checkbox"
              checked={picked.includes(s)}
              disabled={disabled}
              onChange={(e) => setPicked((p) => SERVICE_KEYS.filter((x) => (x === s ? e.target.checked : p.includes(x))))}
              data-testid={`svc-${s}`}
            />
            <label htmlFor={`${uid}-${s}`}>{t(`vol.service.${s}`)}</label>
          </li>
        ))}
      </ul>
      <p className="help">{t('vol171.services.help')}</p>
      <Button size="sm" disabled={disabled || !changed} onClick={() => onSave(picked)} data-testid="svc-save">
        {t('vol171.services.save')}
      </Button>
    </section>
  );
}

/** Under the count: who is ticked for the service but not Ready, what they lack, and a way to each one's page. */
export function AlmostLine({ almost, service, testId }: { almost: AlmostView[] | undefined; service: ServiceKey | null; testId: string }) {
  const { t } = useTranslation();
  if (!almost?.length) return null;
  const what = missingCounts(almost)
    .map((c) => `${t(`vol171.item.${c.item}`)} (${c.count})`)
    .join(', ');
  return (
    <div className="almost" data-testid={testId}>
      <p>
        <b>{service ? t('vol171.almost', { count: almost.length, service: t(`vol.service.${service}`), what }) : t('vol171.almostAny', { count: almost.length, what })}</b>
      </p>
      <p className="help">{t('vol171.almostHelp')}</p>
      <ul>
        {almost.map((a) => (
          <li key={a.volunteerId}>
            {/* a new tab: the request stays as it is */}
            <a href={`/volunteers/${vid(a.volunteerId)}`} target="_blank" rel="noreferrer" data-testid={`almost-${a.volunteerId}`}>
              {a.name}
              <span className="sr-only"> {t('vol171.newTab')}</span>
            </a>
            <span className="help"> · {a.missing.map((m) => t(`vol171.item.${m}`)).join(', ')}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function useOutingPicker(audience: 'events' | 'all') {
  return useQuery({ queryKey: ['volunteer-outing-picker', audience], queryFn: () => apiFetch<OutingPickerView>(`/api/volunteers/outing-picker?audience=${audience}`), staleTime: 10_000 });
}
